/**
 * Layout Component
 * 
 * Main layout wrapper with navigation and header
 */

import { useState, useEffect } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { isDarkTheme, setDarkTheme } from '../lib/theme'
import { useAnalytics } from '../hooks/useAnalytics'
import { Button } from './ui/button'
import { Label } from './ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import DraftIndicator from './DraftIndicator'
import { ChevronDown, Search } from 'lucide-react'
import { visibleNavigation, filterNavigation, reportPageDestination } from '../config/navigation'
import { orderClinicsForPicker } from '../utils/clinicOrder'
import { roleLabel } from '../lib/roles'

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  
  const { user, logout, clinics, currentClinicId, switchClinic, isImpersonating } = useAuthStore()

  // Initialize analytics tracking
  const { trackLogout } = useAnalytics()

  // The theme itself lives in lib/theme; this only mirrors it for the toggle
  // icon, including when the system setting flips underneath us.
  useEffect(() => {
    const sync = () => setIsDarkMode(isDarkTheme())
    sync()
    window.addEventListener('themechange', sync)
    return () => window.removeEventListener('themechange', sync)
  }, [])

  // Redirect tv_display users to the TV display page
  useEffect(() => {
    if (user?.role === 'tv_display') {
      navigate('/tv', { replace: true })
    }
  }, [user?.role, navigate])

  const toggleTheme = () => setDarkTheme(!isDarkMode)

  const handleLogout = async () => {
    trackLogout() // Track logout event before clearing session
    await logout()
    navigate('/login')
  }

  const handleClinicSwitch = async (clinicId) => {
    try {
      // Convert "all" to null for the auth store
      const targetClinicId = clinicId === 'all' ? null : clinicId
      await switchClinic(targetClinicId)
      
      // Update URL query parameter to keep it in sync
      const currentPath = location.pathname
      const newSearchParams = new URLSearchParams(location.search)
      
      if (clinicId === 'all') {
        newSearchParams.delete('clinic')
      } else {
        newSearchParams.set('clinic', clinicId)
      }
      
      // Navigate to same page with updated clinic parameter
      navigate(`${currentPath}?${newSearchParams.toString()}`, { replace: true })
      
      console.log('[CLINIC SWITCH] Switched to clinic:', clinicId, 'URL updated')
    } catch (error) {
      console.error('Failed to switch clinic:', error)
    }
  }

  // Favicons come from the clinic's own websiteDomain, which the API returns
  // alongside every clinic. It used to be a hardcoded id → domain map here,
  // which silently went stale: every clinic added after it was written (Balance,
  // VigoreMD, EvaMD, Revora, Bloom) fell through to the emoji fallback.
  function clinicFavicon(clinic) {
    if (!clinic?.websiteDomain) return null
    return `https://www.google.com/s2/favicons?domain=${clinic.websiteDomain}&sz=32`
  }

  // The sidebar is defined in src/config/navigation.js. It used to be five
  // array literals right here, each with its own roles list and clinic gating
  // spread inline, which is how two role mistakes went unnoticed for so long.
  const navTree = visibleNavigation({ user, clinics, currentClinicId })

  const reportDestination = reportPageDestination(user, navTree, location.pathname)

  const currentClinic = clinics.find(c => c.id === currentClinicId)

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile Sidebar - Full screen drawer */}
      <div 
        className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-0 z-50 lg:hidden transition-transform duration-300 ease-in-out`}
      >
        <div className="flex flex-col h-full w-full bg-background">
          {/* Header with close button */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-border">
            <span className="text-lg font-semibold text-foreground">Menu</span>
            <button
              type="button"
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Navigation items */}
          <nav className="flex flex-col flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            <NavTree
              tree={navTree}
              location={location}
              variant="mobile"
              onNavigate={() => setSidebarOpen(false)}
            />
          </nav>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0">
        <SidebarContent tree={navTree} location={location} />
      </div>

      {/* Main content. min-w-0, or a wide table widens the whole page instead of scrolling in its card. */}
      <div className="lg:pl-64 flex flex-col flex-1 min-w-0">
        {/* Header - combined with mobile hamburger */}
        <header className="sticky top-0 z-10 bg-background shadow-sm border-b border-border">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              <div className="flex items-center gap-2 sm:gap-4">
                {/* Mobile hamburger menu */}
                <button
                  type="button"
                  className="lg:hidden -ml-1 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                  onClick={() => setSidebarOpen(true)}
                >
                  <span className="sr-only">Open sidebar</span>
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                {/* Clinic Selector - shown for super_admin or users with multiple clinics */}
                {(user?.role === 'super_admin' || clinics.length > 1) && clinics.length > 0 && (
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <Label className="text-xs sm:text-sm text-muted-foreground font-medium hidden xs:inline">Clinic:</Label>
                    <Select value={currentClinicId || 'all'} onValueChange={handleClinicSwitch}>
                      <SelectTrigger className="w-32 sm:w-48">
                        <SelectValue placeholder="Select clinic..." />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Show "All Clinics" for super_admin or users with multiple clinics */}
                        {(user?.role === 'super_admin' || clinics.length > 1) && (
                          <SelectItem value="all">
                            <div className="flex items-center space-x-2">
                              <span>🏢</span>
                              <span>All Clinics</span>
                            </div>
                          </SelectItem>
                        )}
                        {orderClinicsForPicker(clinics).map(clinic => (
                          <SelectItem key={clinic.id} value={clinic.id}>
                            <div className="flex items-center space-x-2">
                              <ClinicIcon faviconUrl={clinicFavicon(clinic)} />
                              <span>{clinic.displayName}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Current Clinic Display for users with single clinic */}
                {currentClinic && user?.role !== 'super_admin' && clinics.length === 1 && (
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium">{currentClinic.displayName}</span>
                  </div>
                )}
                
                {isImpersonating && (
                  <div className="flex items-center space-x-2 px-3 py-1 bg-amber-100 border border-amber-300 dark:bg-amber-900 dark:border-amber-700 rounded-md text-sm text-foreground">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.994-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span className="font-medium">Impersonating User</span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Draft Indicator */}
                <DraftIndicator />

                {/* User Menu */}
                <div className="flex items-center space-x-3">
                  {/* Theme Toggle */}
                  <Button
                    onClick={toggleTheme}
                    variant="ghost"
                    size="icon"
                    title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {isDarkMode ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                      </svg>
                    )}
                  </Button>
                  
                  {/* Hide user details text on mobile, show only on larger screens */}
                  <div className="text-right hidden sm:block">
                    <div className="text-sm font-medium text-foreground">{user?.fullName}</div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {roleLabel(user?.role)}
                    </div>
                  </div>
                  <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-primary/80 to-primary rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                    <span className="text-xs sm:text-sm font-semibold text-primary-foreground">
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </span>
                  </div>
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    size="icon"
                    title="Sign out"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {reportDestination === null ? (
              <div className="text-center py-12">
                <h1 className="text-xl font-semibold">No pages available</h1>
                <p className="text-muted-foreground mt-2">Ask your administrator to assign pages for this clinic, or select another assigned clinic.</p>
              </div>
            ) : reportDestination ? (
              <Navigate to={reportDestination} replace />
            ) : children}
          </div>
        </main>
      </div>
    </div>
  )
}

// Clinic favicon with emoji fallback
function ClinicIcon({ faviconUrl }) {
  const [failed, setFailed] = useState(false)
  if (!faviconUrl || failed) return <span className="text-sm">🏥</span>
  return (
    <img
      src={faviconUrl}
      alt=""
      className="w-4 h-4 rounded-sm"
      onError={() => setFailed(true)}
    />
  )
}

/**
 * Per-variant class sets.
 *
 * Mobile is a full-screen drawer with bigger tap targets; desktop is a fixed
 * rail with a left active-border. Everything else about the two is identical,
 * so they share one renderer rather than the two hand-maintained copies this
 * replaced — which had already drifted (mobile never collapsed anything).
 */
const NAV_STYLES = {
  desktop: {
    group: 'w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium border-l-4 rounded-r-lg transition-colors',
    groupActive: 'bg-primary/10 border-primary text-primary dark:bg-primary/20',
    groupIdle: 'border-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
    item: 'flex items-center px-3 py-2 text-sm font-medium border-l-4 rounded-r-lg transition-colors',
    itemActive: 'bg-primary/10 border-primary text-primary dark:bg-primary/20',
    itemIdle: 'border-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
    icon: 'mr-3 h-4 w-4 shrink-0',
  },
  mobile: {
    group: 'w-full flex items-center justify-between px-4 py-3 text-base font-medium rounded-lg transition-colors',
    groupActive: 'bg-primary text-primary-foreground',
    groupIdle: 'text-foreground hover:bg-muted',
    item: 'flex items-center px-4 py-3 text-base font-medium rounded-lg transition-colors',
    itemActive: 'bg-primary text-primary-foreground',
    itemIdle: 'text-foreground hover:bg-muted',
    icon: 'mr-4 h-5 w-5 shrink-0',
  },
}

/**
 * The sidebar tree, rendered.
 *
 * Groups collapse; the one containing the current page starts open. Typing in
 * the filter box expands everything that matched, because a hit hidden inside a
 * collapsed group is the same as no hit. With ~50 destinations for a super
 * admin, searching beats remembering which group something lives in.
 */
function NavTree({ tree, location, variant, onNavigate }) {
  const style = NAV_STYLES[variant]
  const [query, setQuery] = useState('')
  const searching = query.trim().length > 0
  const filtered = filterNavigation(tree, query)

  const activeGroupId = tree.groups.find(group =>
    group.sections.some(section => section.items.some(item => item.href === location.pathname))
  )?.id

  const [openIds, setOpenIds] = useState(() => new Set(activeGroupId ? [activeGroupId] : []))

  // Landing inside a collapsed group — via a filter hit, a redirect, or a link
  // from a page — should leave it open once the filter clears.
  useEffect(() => {
    if (!activeGroupId) return
    setOpenIds(prev => (prev.has(activeGroupId) ? prev : new Set(prev).add(activeGroupId)))
  }, [activeGroupId])

  const toggle = (id) => setOpenIds(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  const renderItem = (item) => {
    const Icon = item.icon
    const isActive = location.pathname === item.href
    return (
      <Link
        key={item.href}
        to={item.href}
        onClick={onNavigate}
        className={`${isActive ? style.itemActive : style.itemIdle} ${style.item}`}
      >
        <Icon className={style.icon} aria-hidden="true" />
        {item.name}
      </Link>
    )
  }

  const renderGroup = (group) => {
    const isOpen = searching || openIds.has(group.id)
    const GroupIcon = group.icon
    return (
      <div key={group.id} className="pt-2">
        <button
          type="button"
          onClick={() => toggle(group.id)}
          aria-expanded={isOpen}
          className={`${group.id === activeGroupId ? style.groupActive : style.groupIdle} ${style.group}`}
        >
          <span className="flex items-center">
            <GroupIcon className={style.icon} aria-hidden="true" />
            {group.name}
          </span>
          <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="ml-4 mt-1 space-y-1">
            {group.sections.map((section, index) => (
              <div key={section.name || index} className="space-y-1">
                {/* Only Analytics carries sub-headers; every other group has a
                    single unnamed section and renders as a flat list. */}
                {section.name && (
                  <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {section.name}
                  </div>
                )}
                {section.items.map(renderItem)}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const pinned = filtered.groups.filter(group => group.pinToBottom)
  const inline = filtered.groups.filter(group => !group.pinToBottom)
  const empty = !filtered.dashboard && filtered.groups.length === 0

  return (
    <>
      <div className="px-3 pb-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            aria-label="Filter navigation"
            className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {filtered.dashboard && renderItem(filtered.dashboard)}
      {inline.map(renderGroup)}

      {empty && (
        <p className="px-3 py-6 text-sm text-muted-foreground">
          Nothing matches “{query.trim()}”.
        </p>
      )}

      {/* Admin sits at the bottom of the rail, below the fold of everyday work. */}
      {pinned.length > 0 && (
        <>
          <div className="flex-1" />
          <div className="mt-4 pt-2 border-t border-border">
            {pinned.map(renderGroup)}
          </div>
        </>
      )}
    </>
  )
}

// Sidebar content component
function SidebarContent({ tree, location }) {
  return (
    <div className="flex-1 flex flex-col min-h-0 bg-card border-r border-border">
      <div className="flex-1 flex flex-col pt-6 pb-4 overflow-y-auto">
        <div className="flex items-center flex-shrink-0 px-4">
          <div
            className="w-8 h-8 bg-foreground"
            style={{
              WebkitMaskImage: 'url(/5axis-logo.svg)',
              WebkitMaskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskImage: 'url(/5axis-logo.svg)',
              maskSize: 'contain',
              maskRepeat: 'no-repeat',
              maskPosition: 'center'
            }}
            aria-label="5axis"
          />
          <span className="ml-3 text-xl font-semibold text-card-foreground">Management</span>
        </div>

        <nav className="mt-6 flex-1 px-3 space-y-1 flex flex-col">
          <NavTree tree={tree} location={location} variant="desktop" />
        </nav>
      </div>

      {/* Powered by 5 Axis Health footer */}
      <div className="flex-shrink-0 px-4 py-4 border-t border-border">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Powered by</span>
        <a
          href="https://5axis.health"
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-2 hover:opacity-80 transition-opacity"
        >
          <div
            className="h-5 w-28 bg-foreground"
            style={{
              WebkitMaskImage: 'url(/5axis-health.svg)',
              WebkitMaskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              WebkitMaskPosition: 'left',
              maskImage: 'url(/5axis-health.svg)',
              maskSize: 'contain',
              maskRepeat: 'no-repeat',
              maskPosition: 'left'
            }}
            aria-label="5 Axis Health"
          />
        </a>
      </div>
    </div>
  )
}
